import { Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { API_BASE_URL_SERVICE } from '../../../../../projects/kiene-core/src/lib/kiene-core.config';
import { MessageService } from '../../../../../projects/kiene-core/src/lib/services/message-service/message.service';
import { Supplier } from '../../../warehouse/supplier/supplier';
import { Material, einkaufAufwandskonto } from '../../../warehouse/material/material';
import { KieneTableService } from '../../../components/kiene-table/kiene-table.service';
import { NebenkostenLieferantPickerDialogComponent } from '../../gemeinkosten/gemeinkosten-invoice-review/nebenkosten-lieferant-picker-dialog.component';
import { NebenkostenArtikelPickerDialogComponent } from '../../gemeinkosten/gemeinkosten-invoice-review/nebenkosten-artikel-picker-dialog.component';
import { NebenkostenPrimanotaVorschauDialogComponent } from '../../gemeinkosten/gemeinkosten-invoice-review/nebenkosten-primanota-vorschau-dialog.component';
import {
    PrimanotaExportVorschauData,
    PrimanotaExportVorschauVariante,
    PrimanotaExportVorschauZeile,
} from '../../gemeinkosten/gemeinkosten-invoice-review/nebenkosten-primanota-vorschau-dialog.models';
import { GutschriftInvoiceReviewDialogData } from './gutschrift-invoice.models';
import { GutschriftPreis, groupGutschriftPreise } from '../gutschrift';
import {
    FIBU_MWST_SAETZE,
    mwstEuroForTargetBrutto,
    nettoFromBrutto,
    roundEuro2,
} from '../../gemeinkosten/gemeinkosten';

interface GutschriftMwstPosition {
    kosten_netto: number;
    mwstsatz: number;
    mwstEuro: number;
}

@Component({
    selector: 'kiene-gutschrift-invoice-review-dialog',
    templateUrl: './gutschrift-invoice-review-dialog.component.html',
    styleUrls: ['./gutschrift-invoice-review-dialog.component.scss'],
})
export class GutschriftInvoiceReviewDialogComponent implements OnInit, OnDestroy {
    mwstSaetze = FIBU_MWST_SAETZE;
    @ViewChild('pdfPane') pdfPane: ElementRef<HTMLDivElement>;

    /** Gutschriften = immer Gesamtbetrag-Modus (wie Gemeinkosten-Checkbox). */
    nurGesamtbetrag = true;

    lieferant: Supplier;
    gesamtArtikel: Material = null;
    gesamtBezeichnung = '';
    gesamtKontoNr: string = null;

    lieferantParams = new HttpParams().set('nebenkosten', '1');
    artikelParams = new HttpParams().set('nebenkosten', '1').set('honorar', '0');

    gutschriftNrCtrl = new FormControl('', Validators.required);
    datumCtrl = new FormControl(new Date(), Validators.required);
    kommentarCtrl = new FormControl('');

    mwstPositionen: GutschriftMwstPosition[] = [{ kosten_netto: 0, mwstsatz: 19, mwstEuro: 0 }];

    extractNetto: number | null = null;
    extractBrutto: number | null = null;

    pdfSrc: Uint8Array | null = null;
    pdfObjectUrl: string | null = null;
    page = 1;
    totalPages = 0;
    pdfLoading = false;
    warnings: string[] = [];
    saving = false;

    constructor(
        public dialogRef: MatDialogRef<GutschriftInvoiceReviewDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: GutschriftInvoiceReviewDialogData,
        @Inject(API_BASE_URL_SERVICE) private readonly apiBaseUrl: string,
        private readonly http: HttpClient,
        private readonly api: KieneTableService,
        private readonly dialog: MatDialog,
        private readonly messageService: MessageService,
    ) {}

    get gesamtNetto(): number {
        return roundEuro2(this.mwstPositionen.reduce((s, p) => s + (Number(p.kosten_netto) || 0), 0));
    }

    get gesamtMwstEuro(): number {
        return roundEuro2(this.mwstPositionen.reduce((s, p) => s + (Number(p.mwstEuro) || 0), 0));
    }

    get mwstsatz(): number {
        return this.dominantMwst();
    }

    get gesamtBrutto(): number {
        return roundEuro2((Number(this.gesamtNetto) || 0) + (Number(this.gesamtMwstEuro) || 0));
    }

    /** Abweichung Beleg-Brutto (OCR) vs. berechnetes Brutto – für Hinweis. */
    get bruttoRundungsDiff(): number | null {
        if (this.extractBrutto == null) {
            return null;
        }
        return roundEuro2(this.extractBrutto - this.gesamtBrutto);
    }

    ngOnInit(): void {
        const d = this.data?.draft;
        const g = this.data?.gutschrift;
        this.warnings = [...(d?.warnings ?? [])];
        if (d?.splitCount && d.splitCount > 1) {
            const idx = (d.splitIndex ?? 0) + 1;
            this.warnings.unshift(`Beleg ${idx} von ${d.splitCount} aus Sammel-PDF`);
        }

        if (g?.lieferant_id) {
            this.lieferant = { lieferant_id: g.lieferant_id, bezeichnung: g.lieferant } as Supplier;
        } else if (d?.suggestedLieferantId) {
            this.lieferant = {
                lieferant_id: d.suggestedLieferantId,
                bezeichnung: d.suggestedLieferantName || '',
            } as Supplier;
        }
        this.hydrateLieferantFibu();

        if (g?.artikel_id) {
            this.gesamtArtikel = {
                artikel_id: g.artikel_id,
                bezeichnung: g.artikel,
                artikel_me: g.artikel,
            } as Material;
        } else if (d?.suggestedArtikelId) {
            this.gesamtArtikel = {
                artikel_id: d.suggestedArtikelId,
                bezeichnung: d.suggestedArtikelMe || '',
                artikel_me: d.suggestedArtikelMe || '',
            } as Material;
        }
        // Stub hat oft kein buchungskonto → nachladen, sonst bleibt „Konto“ leer
        this.hydrateGesamtArtikel();

        this.gutschriftNrCtrl.setValue(g?.gutschrift_nr || d?.suggestedRechnungNr || d?.documentKey || '');
        this.kommentarCtrl.setValue(g?.kommentar || d?.kommentar || '');
        this.gesamtBezeichnung = g?.kommentar || d?.kommentar || d?.suggestedArtikelMe || '';

        const rawDatum = g?.datum || d?.suggestedDatum;
        if (rawDatum) {
            this.datumCtrl.setValue(new Date(rawDatum));
        }

        const saved = (g?.preise?.length ?? 0) > 0 ? g.preise! : null;
        const fromDraft = groupGutschriftPreise(d?.preise);
        const rows: GutschriftPreis[] =
            saved ??
            (fromDraft.length > 0
                ? fromDraft
                : [
                      {
                          kosten_netto:
                              Number(g?.betrag ?? d?.extract?.nettoGesamt ?? 0) || 0,
                          mwstsatz: this.normalizeMwstSatz(g?.mwstsatz ?? d?.extract?.mwstSatz ?? 19),
                      },
                  ]);
        this.mwstPositionen = rows.map(r => this.toMwstPosition(r));

        this.extractNetto =
            d?.extract?.nettoGesamt != null ? Number(d.extract.nettoGesamt) : null;
        this.extractBrutto =
            d?.extract?.bruttoGesamt != null ? Number(d.extract.bruttoGesamt) : null;

        // Beleg-Brutto oft „richtig“ gerundet → MwSt um ±1–2 Cent an der größten Position anpassen
        if (this.extractBrutto != null) {
            const diffCents = Math.round((this.extractBrutto - this.gesamtBrutto) * 100);
            if (diffCents !== 0 && Math.abs(diffCents) <= 2) {
                this.applyBruttoToLargest(this.extractBrutto);
            }
        }

        this.initPdfViewer();
    }

    ngOnDestroy(): void {
        this.revokePdfUrl();
    }

    setLieferant(s: Supplier) {
        this.lieferant = s;
        this.hydrateLieferantFibu();
    }

    openLieferantPicker(): void {
        const initial = (this.lieferant?.bezeichnung || '').trim();
        this.dialog
            .open(NebenkostenLieferantPickerDialogComponent, {
                width: '640px',
                maxWidth: '96vw',
                data: { initialSearch: initial },
            })
            .afterClosed()
            .subscribe((s: Supplier | null) => {
                if (s) {
                    this.setLieferant(s);
                }
            });
    }

    setGesamtArtikel(m: Material) {
        this.gesamtArtikel = m;
        if (m?.artikel_me && !(this.gesamtBezeichnung || '').trim()) {
            this.gesamtBezeichnung = m.artikel_me;
        }
        this.resolveGesamtKonto();
    }

    openGesamtArtikelPicker(): void {
        const initial =
            (this.gesamtBezeichnung || '').trim() ||
            (this.gesamtArtikel?.artikel_me || '').trim() ||
            '';
        this.dialog
            .open(NebenkostenArtikelPickerDialogComponent, {
                width: '640px',
                maxWidth: '96vw',
                data: { initialSearch: initial },
            })
            .afterClosed()
            .subscribe((m: Material | null) => {
                if (m) {
                    this.setGesamtArtikel(m);
                }
            });
    }

    onPositionNettoChanged(p: GutschriftMwstPosition) {
        p.mwstEuro = roundEuro2(((Number(p.kosten_netto) || 0) * (Number(p.mwstsatz) || 0)) / 100);
    }

    onPositionMwstChanged(p: GutschriftMwstPosition) {
        this.onPositionNettoChanged(p);
    }

    onPositionMwstEuroChanged(p: GutschriftMwstPosition) {
        const netto = Number(p.kosten_netto) || 0;
        const mwst = Number(p.mwstEuro) || 0;
        if (netto <= 0) {
            return;
        }
        const implied = Math.round((mwst / netto) * 10000) / 100;
        const snapped = this.snapMwstSatz(Math.round(implied));
        if (Math.abs(implied - snapped) < 0.15) {
            p.mwstsatz = snapped;
        }
    }

    addMwstPosition() {
        const last = this.mwstPositionen[this.mwstPositionen.length - 1];
        const nextMwst = last?.mwstsatz === 19 ? 7 : 19;
        const pos: GutschriftMwstPosition = { kosten_netto: 0, mwstsatz: nextMwst, mwstEuro: 0 };
        this.mwstPositionen.push(pos);
    }

    removeMwstPosition(index: number) {
        if (this.mwstPositionen.length > 1) {
            this.mwstPositionen.splice(index, 1);
        }
    }

    /** Brutto vom Beleg übernehmen / manuell setzen → MwSt an der größten Position anpassen. */
    onGesamtBruttoChanged(brutto: number) {
        this.applyBruttoToLargest(brutto);
    }

    adjustBruttoCent(deltaCents: number) {
        this.onGesamtBruttoChanged(roundEuro2(this.gesamtBrutto + deltaCents / 100));
    }

    applyExtractBrutto() {
        if (this.extractBrutto == null) {
            return;
        }
        this.onGesamtBruttoChanged(this.extractBrutto);
    }

    canMwstVomBetragAbziehen(p: GutschriftMwstPosition): boolean {
        const brutto = Number(p.kosten_netto);
        const mwst = Number(p.mwstsatz);
        return Number.isFinite(brutto) && brutto !== 0 && Number.isFinite(mwst) && mwst > 0;
    }

    /** Netto-Feld enthält oft Brutto: MwSt herausrechnen. */
    mwstVomBetragAbziehen(p: GutschriftMwstPosition) {
        if (!this.canMwstVomBetragAbziehen(p)) {
            return;
        }
        const netto = nettoFromBrutto(p.kosten_netto, p.mwstsatz);
        if (netto != null) {
            p.kosten_netto = netto;
            this.onPositionNettoChanged(p);
        }
    }

    isValid(): boolean {
        return (
            !!this.lieferant?.lieferant_id &&
            this.gutschriftNrCtrl.valid &&
            this.datumCtrl.valid &&
            this.mwstPositionen.length > 0 &&
            this.mwstPositionen.every(p => Number(p.kosten_netto) >= 0 && Number(p.mwstEuro) >= 0)
        );
    }

    openPrimanotaExportVorschau(): void {
        this.dialog.open(NebenkostenPrimanotaVorschauDialogComponent, {
            width: '920px',
            maxWidth: '96vw',
            data: {
                neu: this.buildPrimanotaVorschauNeu(),
                alt: this.buildPrimanotaVorschauAlt(),
                quelle: 'aktuelle Eingabe im Gutschrift-Review',
                neuLabel: 'Neu (Artikel)',
                altLabel: 'Alt (Lieferant)',
            } as PrimanotaExportVorschauData,
        });
    }

    onPdfLoad(pdf: { numPages?: number }) {
        this.totalPages = pdf?.numPages ?? 0;
        this.pdfLoading = false;
    }

    onPdfError(_err: unknown) {
        this.pdfLoading = false;
        this.messageService.infoMessage('PDF-Anzeige fehlgeschlagen – „PDF öffnen“ nutzen.');
    }

    prevPage() {
        if (this.page > 1) {
            this.page -= 1;
        }
    }

    nextPage() {
        if (this.totalPages && this.page < this.totalPages) {
            this.page += 1;
        }
    }

    reloadPdf() {
        this.initPdfViewer();
    }

    openPdfInNewTab() {
        if (this.pdfObjectUrl) {
            window.open(this.pdfObjectUrl, '_blank');
        }
    }

    save() {
        if (!this.isValid()) {
            this.messageService.infoMessage('Lieferant, Betrag und Datum sind erforderlich.');
            return;
        }
        this.saving = true;
        const fd = new FormData();
        const gid = this.data?.gutschrift?.gutschrift_id;
        if (gid) {
            fd.append('gutschrift_id', String(gid));
        }
        fd.append('lieferant_id', String(this.lieferant.lieferant_id));
        if (this.gesamtArtikel?.artikel_id) {
            fd.append('artikel_id', String(this.gesamtArtikel.artikel_id));
        }
        const preise = this.buildPreisePayload();
        fd.append('preise', JSON.stringify(preise));
        fd.append('betrag', String(this.gesamtNetto ?? 0));
        fd.append('mwstsatz', String(this.dominantMwst()));
        fd.append('gutschrift_nr', String(this.gutschriftNrCtrl.value ?? ''));
        fd.append('kommentar', String(this.kommentarCtrl.value ?? ''));
        const d = new Date(this.datumCtrl.value);
        fd.append('datum', d.toISOString());
        fd.append('scan_review', '0');
        if (this.data?.draft?.runId) {
            fd.append('invoice_draft_id', this.data.draft.runId);
        }

        const options = { headers: new HttpHeaders('Phito-Type: fileupload') };
        this.http.post(this.apiBaseUrl + 'gutschrift/update.php', fd, options).subscribe({
            next: () => {
                this.saving = false;
                this.messageService.infoMessage('Gutschrift gespeichert.');
                this.dialogRef.close(true);
            },
            error: err => {
                this.saving = false;
                this.messageService.errorMessage('Speichern fehlgeschlagen.', err);
            },
        });
    }

    close() {
        this.dialogRef.close(false);
    }

    private hydrateLieferantFibu() {
        const id = this.lieferant?.lieferant_id;
        if (!id) {
            return;
        }
        if ((this.lieferant as any)?.buchungskonto) {
            return;
        }
        this.api
            .get(this.apiBaseUrl + 'lieferant/read_one.php', new HttpParams().set('lieferant_id', String(id)))
            .subscribe({
                next: (res: any) => {
                    const rec = res?.records?.[0] || res;
                    if (rec?.lieferant_id) {
                        this.lieferant = Object.assign({} as Supplier, this.lieferant, {
                            lieferant_id: rec.lieferant_id,
                            bezeichnung: rec.bezeichnung || this.lieferant?.bezeichnung,
                            buchungskonto: rec.buchungskonto,
                            buchungsgegenkonto: rec.buchungsgegenkonto,
                        });
                    }
                },
            });
    }

    /** Artikel aus Draft/Gutschrift oft nur id+Name – buchungskonto für Konto-Feld nachladen. */
    private hydrateGesamtArtikel() {
        const id = this.gesamtArtikel?.artikel_id;
        if (!id) {
            return;
        }
        if ((this.gesamtArtikel as any)?.buchungskonto) {
            this.resolveGesamtKonto();
            return;
        }
        this.api
            .get(this.apiBaseUrl + 'artikel/read_one.php', new HttpParams().set('artikel_id', String(id)))
            .subscribe({
                next: (res: any) => {
                    const rec = res?.records?.[0] || res;
                    if (rec?.artikel_id) {
                        this.gesamtArtikel = Object.assign({} as Material, this.gesamtArtikel, {
                            artikel_id: rec.artikel_id,
                            bezeichnung: rec.bezeichnung || this.gesamtArtikel?.bezeichnung,
                            artikel_me:
                                rec.artikel_me ||
                                this.gesamtArtikel?.artikel_me ||
                                rec.bezeichnung ||
                                '',
                            buchungskonto: rec.buchungskonto,
                            laborartikel: rec.laborartikel,
                            verbrauch: rec.verbrauch,
                            nicht_abgebbar: rec.nicht_abgebbar,
                        } as any);
                    }
                    this.resolveGesamtKonto();
                },
                error: () => this.resolveGesamtKonto(),
            });
    }

    private resolveGesamtKonto() {
        this.gesamtKontoNr = null;
        const kontoNr = ((this.gesamtArtikel as any)?.buchungskonto || '').trim();
        if (!kontoNr) {
            return;
        }
        this.api
            .get(
                this.apiBaseUrl + 'fibu_konto/read.php',
                new HttpParams().set('aktiv', '1').set('limit', '5').set('search', kontoNr),
            )
            .subscribe({
                next: (res: any) => {
                    const records: any[] = res?.records || [];
                    const exact =
                        records.find(r => r.konto_nr === kontoNr) ||
                        records.find(r => r.sachkonto === kontoNr && !r.kostenstelle_suffix);
                    this.gesamtKontoNr = exact?.konto_nr || kontoNr;
                },
                error: () => {
                    this.gesamtKontoNr = kontoNr;
                },
            });
    }

    private toMwstPosition(r: GutschriftPreis): GutschriftMwstPosition {
        const netto = Number(r.kosten_netto) || 0;
        const mwstsatz = this.normalizeMwstSatz(r.mwstsatz);
        return {
            kosten_netto: netto,
            mwstsatz,
            mwstEuro: roundEuro2((netto * mwstsatz) / 100),
        };
    }

    private buildPreisePayload(): { kosten_netto: number; mwstsatz: number }[] {
        return this.mwstPositionen.map(p => ({
            kosten_netto: roundEuro2(Number(p.kosten_netto) || 0),
            mwstsatz: Number(p.mwstsatz) || 19,
        }));
    }

    private dominantMwst(): number {
        const payload = this.buildPreisePayload();
        if (!payload.length) {
            return 19;
        }
        return payload.reduce((best, p) =>
            Math.abs(p.kosten_netto) > Math.abs(best.kosten_netto) ? p : best,
        ).mwstsatz;
    }

    private applyBruttoToLargest(targetBrutto: number) {
        const others = this.mwstPositionen
            .slice()
            .sort((a, b) => Math.abs(Number(b.kosten_netto) || 0) - Math.abs(Number(a.kosten_netto) || 0));
        const largest = others[0];
        if (!largest) {
            return;
        }
        const restBrutto = this.mwstPositionen
            .filter(p => p !== largest)
            .reduce((s, p) => s + (Number(p.kosten_netto) || 0) + (Number(p.mwstEuro) || 0), 0);
        largest.mwstEuro = mwstEuroForTargetBrutto(largest.kosten_netto, roundEuro2(targetBrutto - restBrutto));
    }

    private buildPrimanotaVorschauNeu(): PrimanotaExportVorschauVariante {
        const hinweise: string[] = [];
        const gegenkonto = (this.lieferant?.buchungskonto || '').trim();
        if (!gegenkonto) {
            hinweise.push('Kein Kreditor-Konto am Lieferanten – Gegenkonto fehlt.');
        }
        if (!this.lieferant?.lieferant_id) {
            hinweise.push('Kein Lieferant gewählt.');
        }

        const beleg = this.primanotaBelegId();
        const belegdatum = this.formatPrimanotaBelegdatum(this.datumCtrl.value);
        const kopfText = this.buildPrimanotaBuchungstext();
        const konto =
            einkaufAufwandskonto(this.gesamtArtikel, this.lieferant?.buchungsgegenkonto, {
                artikelKonten: true,
            }) ||
            (this.gesamtKontoNr || '').trim();
        const text = this.withBelegIdSuffix(
            (this.kommentarCtrl.value || '').trim() ||
                (this.gesamtBezeichnung || '').trim() ||
                (this.gesamtArtikel?.artikel_me || '').trim() ||
                kopfText,
        );

        const zeilen: PrimanotaExportVorschauZeile[] = [];
        if (!konto) {
            hinweise.push(`Position ohne Aufwandskonto: ${text || '–'}`);
        } else {
            for (const p of this.mwstPositionen) {
                const n = Math.abs(Number(p.kosten_netto) || 0);
                const m = Number(p.mwstsatz) || 0;
                const brutto = Math.round((n + (n * m) / 100) * 100) / 100;
                if (brutto > 0) {
                    zeilen.push({
                        sollhaben: 'H',
                        umsatz: brutto,
                        konto,
                        gegenkonto,
                        bu_schluessel: this.buSchluesselFromMwst(m),
                        belegdatum,
                        beleg,
                        buchungstext: text,
                    });
                }
            }
        }

        if (!zeilen.length && !hinweise.length) {
            hinweise.push('Keine Positionen mit Betrag vorhanden.');
        }

        return {
            zeilen,
            hinweise: [...new Set(hinweise)],
            beschreibung:
                'Neu (Artikel, ab 1.1.): Umsatz/Weiterberechnung 3400 (auch Labor), Praxisverbrauch 4981, Laborverbrauch 4982; sonst Stamm-buchungskonto – Gutschrift als Haben-Zeile.',
        };
    }

    private buildPrimanotaVorschauAlt(): PrimanotaExportVorschauVariante {
        const hinweise: string[] = [];
        const konto = (this.lieferant?.buchungsgegenkonto || '').trim();
        const gegenkonto = (this.lieferant?.buchungskonto || '').trim();
        if (!konto) {
            hinweise.push('Kein Aufwand-Gegenkonto am Lieferanten (buchungsgegenkonto).');
        }
        if (!gegenkonto) {
            hinweise.push('Kein Kreditor-Konto am Lieferanten.');
        }

        const beleg = this.primanotaBelegId();
        const belegdatum = this.formatPrimanotaBelegdatum(this.datumCtrl.value);
        const buchungstext = this.withBelegIdSuffix(
            (this.kommentarCtrl.value || '').trim() || this.buildPrimanotaBuchungstext(),
        );
        const zeilen: PrimanotaExportVorschauZeile[] = [];
        for (const p of this.mwstPositionen) {
            const n = Math.abs(Number(p.kosten_netto) || 0);
            const m = Number(p.mwstsatz) || 0;
            const brutto = Math.round((n + (n * m) / 100) * 100) / 100;
            if (brutto > 0 && konto) {
                zeilen.push({
                    sollhaben: 'H',
                    umsatz: brutto,
                    konto,
                    gegenkonto,
                    bu_schluessel: this.buSchluesselLegacyFromMwst(m),
                    belegdatum,
                    beleg,
                    buchungstext,
                });
            }
        }
        if (!zeilen.length && !hinweise.length) {
            hinweise.push('Keine Positionen mit Betrag vorhanden.');
        }

        return {
            zeilen,
            hinweise: [...new Set(hinweise)],
            beschreibung:
                'Alt (Legacy): Lieferanten-Primanota über Gegenkonto, wie bisher beim Erzeugen.',
        };
    }

    private buSchluesselFromMwst(mwst: number): number | null {
        const m = Math.round(Number(mwst) || 0);
        if (m === 19) {
            return 9;
        }
        if (m === 7) {
            return 8;
        }
        return null;
    }

    private buSchluesselLegacyFromMwst(mwst: number): number | null {
        const m = Math.round(Number(mwst) || 0);
        if (m === 19) {
            return 9;
        }
        if (m === 7 || m === 9) {
            return 8;
        }
        if (m === 0) {
            return 0;
        }
        return null;
    }

    private formatPrimanotaBelegdatum(raw: unknown): string {
        if (!raw) {
            return '';
        }
        const d = raw instanceof Date ? raw : new Date(raw as string);
        if (Number.isNaN(d.getTime())) {
            return '';
        }
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yy = String(d.getFullYear()).slice(-2);
        return `${dd}${mm}${yy}`;
    }

    private buildPrimanotaBuchungstext(): string {
        const kom = (this.kommentarCtrl.value || '').trim();
        if (kom) {
            return this.withBelegIdSuffix(kom);
        }
        const nr = (this.gutschriftNrCtrl.value || '').trim();
        const lief = (this.lieferant?.bezeichnung || '').trim();
        const art =
            (this.gesamtArtikel?.artikel_me || '').trim() || (this.gesamtBezeichnung || '').trim();
        return this.withBelegIdSuffix(`${nr} ${lief || art}`.trim());
    }

    /** Primanota-Beleg = interne Gutschrift-ID (nicht Gutschrift-Nr. vom Lieferanten). */
    private primanotaBelegId(): string {
        const id = this.data?.gutschrift?.gutschrift_id;
        return id ? String(id) : '';
    }

    /** Wie Kosten-Primanota: Beleg-ID hinten als `#1234` für schnelle Suche. */
    private withBelegIdSuffix(text: string): string {
        const id = this.data?.gutschrift?.gutschrift_id;
        const base = (text || '').trim();
        if (!id) {
            return base.substring(0, 250);
        }
        const tag = `#${id}`;
        if (base.includes(tag)) {
            return base.substring(0, 250);
        }
        const room = 250 - tag.length - (base ? 1 : 0);
        const head = base.substring(0, Math.max(0, room)).trimEnd();
        return (head ? `${head} ${tag}` : tag).substring(0, 250);
    }

    private normalizeMwstSatz(v: number): number {
        return this.snapMwstSatz(Number(v) || 19);
    }

    private snapMwstSatz(raw: number): number {
        const candidates = [0, 7, 19];
        let best = 19;
        let bestDiff = Infinity;
        for (const c of candidates) {
            const diff = Math.abs(c - raw);
            if (diff < bestDiff) {
                bestDiff = diff;
                best = c;
            }
        }
        return best;
    }

    private initPdfViewer() {
        this.revokePdfUrl();
        const file = this.data?.pdfFile;
        if (!file) {
            this.pdfSrc = null;
            return;
        }
        this.pdfLoading = true;
        file
            .arrayBuffer()
            .then(buf => {
                this.pdfSrc = new Uint8Array(buf);
                this.pdfObjectUrl = URL.createObjectURL(file);
                this.pdfLoading = false;
            })
            .catch(() => {
                this.pdfLoading = false;
                this.pdfSrc = null;
            });
    }

    private revokePdfUrl() {
        if (this.pdfObjectUrl) {
            URL.revokeObjectURL(this.pdfObjectUrl);
            this.pdfObjectUrl = null;
        }
    }
}
